export const stepsConfig = {
    steps: [
        {
            id: "welcome",
            layout: "welcome",
            title: "Welcome to Caloria",
            subtitle: "Let's calculate your perfect calorie and macro goals",
            button: {
                text: "Get Started",
            },
        },
        {
            id: "gender", // Add this as the first step after welcome
            layout: "single_select",
            title: "What is your gender?",
            subtitle: "We will use this information to personalize your program",
            field: "gender",
            options: [
                {id: "woman", text: "Woman"},
                {id: "man", text: "Man"},
            ],
        },
        {
            id: "focus",
            layout: "single_select",
            title: "To start, tell us what [accent]your focus is right now[/accent]:",
            field: "focus",
            options: [
                {id: "lose_weight", emoji: "🔥", text: "Lose weight"},
                {id: "get_fit", emoji: "💪", text: "Get fit"},
                {id: "feel_healthier", emoji: "🍀", text: "Feel healthier"},
            ],
        },
        {
            id: "focuses",
            layout: "multi_select",
            title: "What areas do you want to focus on in your plan?",
            subtitle: "Select all that apply",
            field: "focuses",
            options: [
                {id: "nutrition", emoji: "🥗", text: "Nutrition"},
                {id: "physical_activity", emoji: "🚴", text: "Physical activity"},
                {id: "habits", emoji: "✅", text: "Building good habits"},
                {id: "other", emoji: "⭐️", text: "Other"},
            ],
        },
        {
            id: "additional_goals",
            layout: "multi_select",
            title: "Is there anything else you want to achieve?",
            subtitle: "Select all that apply",
            field: "additionalGoals",
            options: [
                {
                    id: "food_relationship",
                    emoji: "🥗",
                    text: "Improve my relationship with food",
                },
                {
                    id: "cooking",
                    emoji: "🧑‍🍳",
                    text: "Learn how to cook healthily",
                },
                {
                    id: "immune_system",
                    emoji: "🍋",
                    text: "Strengthen my immune system",
                },
                {
                    id: "sleep_energy",
                    emoji: "🛌",
                    text: "Sleep better and have more energy",
                },
                {
                    id: "none",
                    emoji: "🫧",
                    text: "None of the above",
                },
            ],
        },
        {
            id: "birthday",
            layout: "birthday",
            title: "When is your birthday?",
            subtitle:
                "We need your date of birth to adjust your plan according to your age.",
            description:
                "As you grow older, you need about 30% more protein than when you were younger to keep your muscles strong and healthy.",
            field: "birthday",
            splash: {
                layout: "gender",
                title: "Default title if no gender specified",
                subtitle: "Default subtitle if no gender specified",
                woman: {
                    title:
                        "You're in good hands. Join 1.8M happy women already getting results",
                    subtitle:
                        "Let us take care of you while you take care of yourself. We're here for you all along the way. 💜",
                    imageUrl: "/w-you-are-in-good-hands.webp",
                    buttonText: "Sounds Good",
                },
                man: {
                    title:
                        "You're in good hands. Join 1.2M men already achieving their goals",
                    subtitle:
                        "We'll help you build the body you want. Our proven system gets results. Let's make it happen. 💪",
                    imageUrl: "/m-you-are-in-good-hands.png",
                    buttonText: "Let's Do This",
                },
            },
        },
        {
            id: "height",
            layout: "height",
            title: "How tall are you?",
            subtitle:
                "Please enter your height so we can best calculate your calorie needs.",
            description:
                "BMI, short for Body Mass Index, is a measure of body fat that uses height and weight to predict your risk of developing potential health problems.",
            field: "height",
        },
        {
            id: "weight",
            layout: "weight",
            title: "What's your current weight?",
            subtitle:
                "Please enter your current weight to help us calculate your daily needs.",
            description:
                "Your weight, along with your height and age, helps us determine your basal metabolic rate (BMR) - the number of calories your body burns at rest.",
            field: "weight",
        },
        {
            id: "weight_goal",
            layout: "weight_goal",
            title: "What's your weight goal?",
            subtitle:
                "Enter your target weight to help us create your personalized plan.",
            description:
                "A healthy and sustainable weight loss is typically 0.5-1kg (1-2 lbs) per week. Rapid weight loss can be harmful to your health.",
            field: "weightGoal",
            splash: {
                layout: "gender",
                title: "Default title if no gender specified",
                subtitle: "Default subtitle if no gender specified",
                woman: {
                    title: "Let's set some goal you're going to crush!",
                    subtitle:
                        "Some people prefer to start small while others prefer to set their long-term goal right from the start. We recommend going with what feels most comfortable and motivating for you!",
                    imageUrl: "/w-going-to-crush.png",
                    buttonText: "Next",
                },
                man: {
                    title: "Let's set some goal you're going to crush!",
                    subtitle:
                        "Some people prefer to start small while others prefer to set their long-term goal right from the start. We recommend going with what feels most comfortable and motivating for you!",
                    imageUrl: "/w-going-to-crush.png",
                    buttonText: "Let's Do This",
                },
                backgroundColor: "bg-[#D6D9DE]",
                buttonText: "Next",
            },
        },
        {
            id: "result_preview",
            layout: "image",
            title: "Here's your personalized plan",
            subtitle: "Based on your answers, we've created a custom plan for you",
            imageUrl: "/w-chart.png",
            buttonText: "Got it",
        },
        {
            id: "activity_level",
            layout: "single_select",
            title: "What's your activity level?",
            subtitle:
                "Knowing your daily activity level helps us calculate your calorie needs more accurately.",
            field: "activityLevel",
            options: [
                {
                    id: "sedentary",
                    emoji: "🧑‍💻",
                    title: "Sedentary",
                    subtitle: "Less than 5,000 steps a day",
                },
                {
                    id: "moderate",
                    emoji: "🚶",
                    title: "Moderately active",
                    subtitle: "5,000 - 15,000 steps a day",
                },
                {
                    id: "very_active",
                    emoji: "🏃",
                    title: "Very active",
                    subtitle: "More than 15,000 steps a day",
                },
            ],
        },
        {
            id: "training_activity",
            layout: "single_select",
            title: "Are you currently doing or planning to start any training?",
            subtitle:
                "No pressure! We'll use this information to calculate accurate energy expenditure and create a personalized plan just for you.",
            field: "trainingActivity",
            options: [
                {
                    id: "none",
                    emoji: "🧘",
                    title: "None or relaxed activity",
                    subtitle: "Light stretching or yoga",
                },
                {
                    id: "lifting",
                    emoji: "🏋️",
                    title: "Lifting",
                    subtitle: "Strength training or weightlifting",
                },
                {
                    id: "cardio",
                    emoji: "🏃",
                    title: "Cardio or recreational sports",
                    subtitle: "Running, swimming, team sports",
                },
                {
                    id: "cardio_and_lifting",
                    emoji: "🔥",
                    title: "Cardio & lifting",
                    subtitle: "Combined training program",
                },
            ],
        },
        {
            id: "workout_frequency",
            layout: "single_select",
            title: "How often do you typically work out?",
            subtitle:
                "Choose the total number of recreational sports, cardio, or resistance training sessions you aim for weekly.",
            field: "workoutFrequency",
            options: [
                {
                    id: "low",
                    emoji: "🙂",
                    title: "1-3 sessions / week",
                    subtitle: "Beginner friendly",
                },
                {
                    id: "medium",
                    emoji: "👍",
                    title: "4-6 sessions / week",
                    subtitle: "Intermediate level",
                },
                {
                    id: "high",
                    emoji: "💪",
                    title: "7+ sessions / week",
                    subtitle: "Advanced level",
                },
            ],
        },
        {
            id: "walking_frequency",
            layout: "single_select",
            title: "How often do you go for walks?",
            subtitle: "Regular walking is great for your physical and mental health.",
            field: "walkingFrequency",
            options: [
                {
                    id: "daily",
                    emoji: "💪",
                    title: "Almost every day",
                },
                {
                    id: "frequent",
                    emoji: "😎",
                    title: "3-4 times a week",
                },
                {
                    id: "occasional",
                    emoji: "🙂",
                    title: "1-2 times a week",
                },
                {
                    id: "rare",
                    emoji: "🫥",
                    title: "Maybe once a month",
                },
            ],
        },
        {
            id: "health_concerns",
            layout: "multi_select",
            title: "Do you have any health concerns?",
            subtitle:
                "Select all that apply. This helps us provide safer recommendations.",
            field: "healthConcerns",
            options: [
                {id: "hypertension", emoji: "🫀", text: "Hypertension"},
                {id: "high_cholesterol", emoji: "🥚", text: "High cholesterol"},
                {id: "obesity", emoji: "⚖️", text: "Obesity"},
                {id: "diabetes", emoji: "🍯", text: "Diabetes"},
                {id: "heart_disease", emoji: "💗", text: "Heart disease"},
                {id: "cancer", emoji: "🎗️", text: "Cancer"},
                {id: "lung_disease", emoji: "🫁", text: "Lung disease"},
                {id: "thyroid_disease", emoji: "🦋", text: "Thyroid disease"},
                {id: "gastric_disease", emoji: "�胃", text: "Gastric disease"},
                {id: "none", emoji: "✅", text: "None of the above"},
            ],
        },
        {
            id: "summary",
            layout: "summary",
            title: "Personal Summary based <br>on your answers",
            subtitle: "",
        },
        {
            id: "lifestyle_assessment",
            layout: "lifestyle_assessment",
            title: "",
            subtitle: "",
            field: "lifestyleAssessment",
            options: [
                {
                    id: "needs_work",
                    emoji: "🌱",
                    title: "My diet and activity need a lot of work",
                    subtitle: "I'm ready to make positive changes",
                },
                {
                    id: "some_habits",
                    emoji: "🌿",
                    title: "I have some healthy habits",
                    subtitle: "But there's room for improvement",
                },
                {
                    id: "mostly_healthy",
                    emoji: "🌳",
                    title: "I mostly eat well and stay active",
                    subtitle: "Looking to optimize further",
                },
            ],
        },
        {
            id: "habits",
            layout: "multi_select",
            title: "Do you have any of the following habits?",
            subtitle:
                "Select all that apply. Being honest helps us create a more effective plan.",
            field: "habits",
            options: [
                {
                    id: "wine",
                    emoji: "🍷",
                    text: "I like my wine",
                },
                {
                    id: "late_night_eating",
                    emoji: "💤",
                    text: "I eat late at night",
                },
                {
                    id: "sweet_tooth",
                    emoji: "🧁",
                    text: "I have a raging sweet tooth",
                },
                {
                    id: "snacking",
                    emoji: "🍪",
                    text: "I get snack attacks",
                },
                {
                    id: "soda",
                    emoji: "🥤",
                    text: "I can't live without soda",
                },
                {
                    id: "salty_crunchy",
                    emoji: "🥨",
                    text: "I like all things salty and crunchy",
                },
                {
                    id: "screen_eating",
                    emoji: "💻",
                    text: "I mix screen time with mealtime",
                },
                {
                    id: "none",
                    emoji: "🙅‍♀️",
                    text: "None of the above",
                },
            ],
        },
        {
            id: "snack_cravings",
            layout: "single_select",
            title: "When do snack cravings hit you most?",
            subtitle:
                "By analyzing your eating habits, we provide personalized guidance to make choices that best support your goals.",
            field: "snackCravings",
            options: [
                {
                    id: "mornings",
                    emoji: "🌅",
                    title: "Mornings",
                    subtitle: "Between breakfast and lunch",
                },
                {
                    id: "afternoon",
                    emoji: "☀️",
                    title: "Afternoon",
                    subtitle: "Between lunch and dinner",
                },
                {
                    id: "evenings",
                    emoji: "🌆",
                    title: "Evenings",
                    subtitle: "After dinner",
                },
                {
                    id: "late_night",
                    emoji: "🌙",
                    title: "Late at night",
                    subtitle: "Before bed",
                },
            ],
        },
        {
            id: "snack_triggers",
            layout: "multi_select",
            title: "What typically triggers your snacking and nibbling urges?",
            subtitle:
                "Understanding your triggers helps us provide better strategies to manage cravings.",
            field: "snackTriggers",
            options: [
                {
                    id: "food_nearby",
                    text: "Having food nearby",
                },
                {
                    id: "boredom",
                    text: "Feeling bored",
                },
                {
                    id: "hunger",
                    text: "Being hungry",
                },
                {
                    id: "stress",
                    text: "Stress",
                },
                {
                    id: "social",
                    text: "Other people snacking",
                },
                {
                    id: "other",
                    text: "Something else",
                },
            ],
        },
        {
            id: "emotional_eating",
            layout: "rating_scale",
            title: "Do you relate to the following statement?",
            subtitle: "Eating is one of the ways I usually deal with my emotions",
            field: "emotionalEating",
            minLabel: "Not at all",
            maxLabel: "Totally",
        },
        {
            id: "eating_habits",
            layout: "rating_scale",
            title: "Do you relate to the following statement?",
            subtitle:
                "Multitasking while eating has become habitual for me (like eating while working or scrolling on my phone)",
            field: "eatingHabits",
            minLabel: "Not at all",
            maxLabel: "Totally",
        },
        {
            id: "plate_clearing",
            layout: "rating_scale",
            title: "Do you relate to the following statement?",
            subtitle: "Even if I'm already full, I usually clear my plate",
            field: "plateClearing",
            minLabel: "Not at all",
            maxLabel: "Totally",
        },
        {
            id: "routine_persistence",
            layout: "rating_scale",
            title: "Do you relate to the following statement?",
            subtitle:
                "After a week or two of eating healthier or exercising, I fall back to my old routine",
            field: "routinePersistence",
            minLabel: "Not at all",
            maxLabel: "Totally",
        },
        {
            id: "result_preview2",
            layout: "image",
            title: "Why restrictive diets fail or the «Yo-Yo» effect:",
            imageUrl: "/weight-time-chart.png",
            buttonText: "Got it",
            infoBox: {
                variant: "warning",
                icon: "info",
                title: "Understanding the Yo-Yo Effect",
                text: "Restrictive diets often lead to a cycle of weight loss followed by weight gain. This happens because these diets are unsustainable and don't address the root causes of weight gain.",
                badge: "Important",
            },
        },
        {
            id: "meal_preferences",
            layout: "single_select",
            title: "Which of the following best describes your typical lunch?",
            subtitle:
                "In your twenties, the foods you pair in your meals can have a significant impact on your body's ability to burn fat.",
            field: "mealPreferences",
            headerOrder: "subtitle-title",
            options: [
                {
                    id: "sandwiches",
                    title: "I tend to eat sandwiches or wraps",
                },
                {
                    id: "soups_salads",
                    title: "I tend to eat soups or salads",
                },
                {
                    id: "protein_side",
                    title: "I tend to eat protein with a side",
                },
                {
                    id: "fast_food",
                    title: "I tend to eat fast food",
                },
                {
                    id: "other",
                    title: "Other",
                },
            ],
        },
        {
            id: "carb_frequency",
            layout: "single_select",
            title:
                "How often do you eat [accent]carb-dense foods[/accent] like bread, potatoes, pasta, etc?",
            subtitle: "No judgment: This will help us personalize your plan",
            field: "carbFrequency",
            options: [
                {
                    id: "very_often",
                    title: "Very often",
                },
                {
                    id: "sometimes",
                    title: "Sometimes",
                },
                {
                    id: "rarely",
                    title: "Rarely",
                },
                {
                    id: "not_at_all",
                    title: "Not at all",
                },
            ],
        },
        {
            id: "produce_intake",
            layout: "single_select",
            title: "How's your fruit and vegetable intake?",
            subtitle: "Generally, how many fruits and veggies do you eat a day?",
            field: "produceIntake",
            options: [
                {
                    id: "low",
                    emoji: "🙅‍♀️",
                    title: "None or a little",
                },
                {
                    id: "moderate",
                    emoji: "🍏",
                    title: "A fair bit",
                },
                {
                    id: "high",
                    emoji: "🥕",
                    title: "I might be a rabbit",
                },
            ],
        },
        {
            id: "recent_foods",
            layout: "single_select", // Using multi_select since users might want to select multiple foods
            title: "In the last week, which of the following foods have you eaten?",
            subtitle:
                "We don't follow a \"one-diet-fits-all\" approach, and what you're already eating can likely be adapted to your weight loss goals.",

            field: "recentFoods",
            headerOrder: "subtitle-title",
            options: [
                {
                    id: "chicken",
                    text: "Chicken",
                },
                {
                    id: "avocado",
                    text: "Avocado",
                },
                {
                    id: "white_rice",
                    text: "White rice",
                },
                {
                    id: "eggs",
                    text: "Eggs",
                },
                {
                    id: "none",
                    text: "None of the above",
                },
            ],
        },
        {
            id: "result_preview3",
            layout: "image",
            title: "Learn how to make better food choices",
            imageUrl: "/food-choices.png",
            buttonText: "Let's dive in",
        },
        {
            id: "meal_frequency",
            layout: "single_select",
            title: "What's a typical day<br> look like for you?",
            subtitle:
                "A healthy metabolism that's primed for fat loss is about more than just what you eat - [accent]how often you eat[/accent] is also important.",
            field: "mealFrequency",
            headerOrder: "subtitle-title",
            options: [
                {
                    id: "three_or_less",
                    title: "I almost always eat three or fewer meals per day",
                },
                {
                    id: "three_plus_snacks",
                    title:
                        "I almost always eat at least three meals, plus several snacks a day",
                },
                {
                    id: "variable",
                    title: "It depends — sometimes less than three meals, sometimes more",
                },
            ],
        },
        {
            id: "diet_plan",
            layout: "single_select",
            title: "Which plan best suits your preferences?",
            field: "dietPlan",
            options: [
                {
                    id: "balanced",
                    emoji: "🍽️",
                    text: "Balanced",
                },
                {
                    id: "keto",
                    emoji: "🥑",
                    text: "Keto",
                },
                {
                    id: "vegetarian",
                    emoji: "🥦",
                    text: "Vegetarian",
                },
                {
                    id: "vegan",
                    emoji: "🌱",
                    text: "Fully plant-based",
                },
                {
                    id: "low_carb",
                    emoji: "🥗",
                    text: "Low-carb",
                },
                {
                    id: "paleo",
                    emoji: "🥩",
                    text: "Paleo",
                },
            ],
        },
        {
            id: "food_restrictions",
            layout: "multi_select",
            title: "Do you have any food restrictions?",
            field: "foodRestrictions",
            options: [
                {
                    id: "gluten",
                    emoji: "🌾",
                    text: "Gluten",
                },
                {
                    id: "eggs",
                    emoji: "🥚",
                    text: "Eggs",
                },
                {
                    id: "tree_nuts",
                    emoji: "🥜",
                    text: "Tree Nuts",
                },
                {
                    id: "dairy",
                    emoji: "🥛",
                    text: "Dairy",
                },
                {
                    id: "seafood",
                    emoji: "🍤",
                    text: "Seafood",
                },
            ],
        },
        {
            id: "busyness_level",
            layout: "single_select",
            title: "How busy are you on<br> an average day?",
            field: "busynessLevel",
            options: [
                {
                    id: "very_busy",
                    title: "I barely have any time for myself",
                },
                {
                    id: "moderately_busy",
                    title:
                        "I'm busy but try to reserve some time each day to relax and unwind",
                },
                {
                    id: "somewhat_busy",
                    title: "I'm not too busy and keep time open for different things",
                },
                {
                    id: "not_busy",
                    title: "My schedule is fairly open and flexible",
                },
            ],
        },
        {
            id: "meal_preparation",
            layout: "single_select",
            title: "How do you usually prepare your meals?",
            field: "mealPreparation",
            options: [
                {
                    id: "self_cook",
                    title: "I cook them myself",
                },
                {
                    id: "other_cook",
                    title: "Someone else cooks for me",
                },
                {
                    id: "restaurant",
                    title: "I order from restaurants",
                },
                {
                    id: "premade",
                    title: "I eat premade meals",
                },
            ],
        },
        {
            id: "cooking_skill",
            layout: "single_select",
            title: "How would you describe your cooking skill?",
            subtitle:
                "This helps us recommend recipes that match your experience level.",
            field: "cookingSkill",
            options: [
                {
                    id: "expert",
                    emoji: "👨‍🍳",
                    title: "I'm an expert cook",
                },
                {
                    id: "learning",
                    emoji: "📚",
                    title: "I'm learning to be a better cook",
                },
                {
                    id: "beginner",
                    emoji: "🆕",
                    title: "I don't know how to cook at all",
                },
            ],
        },
        {
            id: "meal_prep_time",
            layout: "single_select",
            title: "How much time are you willing to put in for each meal?",
            field: "mealPrepTime",
            options: [
                {
                    id: "less_than_15",
                    title: "Less than 15 min",
                },
                {
                    id: "fifteen_to_thirty",
                    title: "15-30 min",
                },
                {
                    id: "thirty_to_sixty",
                    title: "30-60 min",
                },
                {
                    id: "more_than_hour",
                    title: "More than 1 hour",
                },
            ],
        },
        {
            id: "nutrition_report",
            layout: "nutrition_report",
            title: "Nutrition report based<br> on your answers",
        },
        {
            id: "weight_loss_knowledge",
            layout: "single_select",
            title: "How familiar are you with the topic of weight loss?",
            subtitle:
                "This helps us provide the right level of guidance and education",
            field: "weightLossKnowledge",
            options: [
                {
                    id: "beginner",
                    emoji: "🐣",
                    title: "Beginner",
                    subtitle: "I'm new to weight loss and need to learn a lot",
                },
                {
                    id: "intermediate",
                    emoji: "🐥",
                    title: "Intermediate",
                    subtitle: "I have some experience but still need guidance",
                },
                {
                    id: "master",
                    emoji: "🦅",
                    title: "Master",
                    subtitle: "I have lots of experience",
                },
            ],
        },
        {
            id: "calorie_knowledge",
            layout: "single_select",
            title: "Do you know the relationship between calories and weight?",
            subtitle:
                "Understanding this relationship is key to achieving your weight goals",
            field: "calorieKnowledge",
            options: [
                {
                    id: "yes",
                    emoji: "👍",
                    title: "Yes, calorie intake affects body weight",
                    subtitle: "I understand how calories impact weight management",
                },
                {
                    id: "somewhat",
                    emoji: "🤔",
                    title: "Somewhat, calories impact weight",
                    subtitle: "I have a basic understanding but could learn more",
                },
                {
                    id: "no",
                    emoji: "🙅‍♂️",
                    title: "No, I'm not sure how calories influence weight",
                    subtitle: "I need to learn about calories and weight management",
                },
            ],
        },
        {
            id: "nutrition_importance",
            layout: "image",
            title: "We know from experience that nutrition is 80% of your results",
            imageUrl: "/nutrition-exercise.png", // You'll need to add this image to your public folder
            buttonText: "I understand",
            infoBox: {
                variant: "info",
                icon: "info",
                title: "Why Nutrition Matters More",
                text: "Research shows that individuals who monitor their calorie intake and prioritize their nutrition are more likely to maintain better physical health and achieve their fitness goals.",
                badge: "Science",
            },
        },
        {
            id: "eating_improvement",
            layout: "single_select",
            title:
                "What's the most important thing you want to improve about the way you eat?",
            field: "eatingImprovement",
            options: [
                {
                    id: "awareness",
                    emoji: "👀",
                    text: "Being more aware of food choices",
                },
                {
                    id: "whole_foods",
                    emoji: "🥗",
                    text: "Focus on whole foods",
                },
                {
                    id: "produce",
                    emoji: "🥑",
                    text: "Aiming for more fruit & veggies",
                },
                {
                    id: "education",
                    emoji: "📚",
                    text: "Learning more about nutrition",
                },
                {
                    id: "temptation",
                    emoji: "🍫",
                    text: "Avoid having tempting food nearby",
                },
                {
                    id: "portions",
                    emoji: "🍽️",
                    text: "Be more mindful about portion sizes",
                },
            ],
        },
        {
            id: "food_tracking",
            layout: "single_select",
            title: "Do you usually track what you eat?",
            field: "foodTracking",
            options: [
                {
                    id: "every_meal",
                    emoji: "🥘",
                    title: "Every meal",
                },
                {
                    id: "sometimes",
                    emoji: "🧐",
                    title: "I do when I remember",
                },
                {
                    id: "never",
                    emoji: "🚫",
                    title: "Not at all",
                },
            ],
        },
        {
            id: "nutrient_tracking",
            layout: "single_select",
            title: "Do you know how many nutrients you usually consume every day?",
            field: "nutrientTracking",
            options: [
                {
                    id: "yes",
                    emoji: "✅",
                    title: "I do!",
                },
                {
                    id: "sometimes",
                    emoji: "📊",
                    title: "I check from time to time",
                },
                {
                    id: "no",
                    emoji: "🚫",
                    title: "Not really",
                },
            ],
        },
        {
            id: "features_preview",
            layout: "features_preview",
            title:
                "Our AI tracker uses technology to make weight loss process painless",
            features: [
                {
                    icon: "🔬",
                    title: "Advanced Analytics",
                    description: "Track your progress with detailed insights",
                },
                {
                    icon: "🤖",
                    title: "Machine Learning",
                    description: "Personalized recommendations that adapt to you",
                },
                {
                    icon: "🔍",
                    title: "Image Recognition",
                    description: "Easily log meals by taking photos",
                },
                {
                    icon: "🌐",
                    title: "Artificial Neural Networks",
                    description: "Smart predictions for better results",
                },
            ],
        },
        {
            id: "weight_loss_diet_knowledge",
            layout: "rating_scale",
            title: "Do you relate to the following statement?",
            subtitle: "I want to lose weight, but I don't know what to eat.",
            field: "weightLossDietKnowledge",
            minLabel: "Not at all",
            maxLabel: "Totally",
        },
        {
            id: "wrong_food_worry",
            layout: "rating_scale",
            title:
                "I always worry about eating the wrong thing, as it could undo all my hard work.",
            subtitle: "Do you relate to the following statement?",
            field: "wrongFoodWorry",
            minLabel: "Not at all",
            maxLabel: "Totally",
            headerOrder: "subtitle-title",
        },
        {
            id: "unhealthy_decisions_spiral",
            layout: "rating_scale",
            subtitle: "Do you relate to the following statement?",
            title:
                "My unhealthy decisions often lead me to make more unhealthy decisions. I feel like a failure because I can't commit to a healthier lifestyle",
            field: "unhealthyDecisionsSpiral",
            minLabel: "Not at all",
            maxLabel: "Totally",
            headerOrder: "subtitle-title",
        },
        {
            id: "weight_goal_feelings",
            layout: "multi_select",
            title: "When I think of reaching my happy weight, I am:",
            field: "weightGoalFeelings",
            options: [
                {
                    id: "energetic",
                    text: "Full of energy each day",
                },
                {
                    id: "confident",
                    text: "More confident",
                },
                {
                    id: "social",
                    text: "More social",
                },
                {
                    id: "self_priority",
                    text: "Putting myself first",
                },
                {
                    id: "explorer",
                    text: "Excited about exploring new things",
                },
                {
                    id: "other",
                    text: "Other",
                },
            ],
        },
        {
            id: "change_priority",
            layout: "single_select",
            title: "When it comes to changing how you feel, what's most important?",
            subtitle: "Choose the outcome that matters most to you",
            field: "changePriority",
            options: [
                {
                    id: "energy",
                    emoji: "🔋",
                    title: "Having more energy",
                },
                {
                    id: "clothes_fit",
                    emoji: "👗",
                    title: "Feeling better in my clothes",
                },
                {
                    id: "confidence",
                    emoji: "💖",
                    title: "Having more confidence",
                },
                {
                    id: "physical_comfort",
                    emoji: "🧘‍♀️",
                    title: "Physically feeling more comfortable",
                },
            ],
        },
        {
            id: "struggles",
            layout: "single_select",
            title: "What do you most struggle with?",
            field: "struggles",
            options: [
                {
                    id: "motivation",
                    emoji: "🏆",
                    text: "Staying motivated",
                },
                {
                    id: "portions",
                    emoji: "🥘",
                    text: "Reducing portion sizes",
                },
                {
                    id: "time",
                    emoji: "⏰",
                    text: "Lack of free time",
                },
                {
                    id: "cravings",
                    emoji: "🍕",
                    text: "Resisting cravings",
                },
                {
                    id: "knowledge",
                    emoji: "🥣",
                    text: "Knowing what to eat",
                },
                {
                    id: "other",
                    emoji: "☕️",
                    text: "Something else",
                },
            ],
        },
        {
            id: "social_impact",
            layout: "rating_scale",
            subtitle: "Do you relate to the following statement?",
            title:
                "My weight has affected my ability to socialize or engage with friends and family.",
            field: "socialImpact",
            minLabel: "Not at all",
            maxLabel: "Totally",
        },
        {
            id: "convenience_priority",
            layout: "rating_scale",
            subtitle: "Do you relate to the following statement?",
            title:
                "I have been thinking about weight loss for a while, but life is so busy I find myself putting convenience first.",
            field: "conveniencePriority",
            minLabel: "Not at all",
            maxLabel: "Totally",
            headerOrder: "subtitle-title",
        },
        {
            id: "personalized_approach",
            layout: "rating_scale",
            subtitle: "Do you relate to the following statement?",
            title:
                "I know what I should be doing to lose weight, but I need a way to do it that fits into MY life.",
            field: "personalizedApproach",
            minLabel: "Not at all",
            maxLabel: "Totally",
            headerOrder: "subtitle-title",
        },
        {
            id: "weight_loss_experience",
            layout: "single_select",
            title: "What experience do you have with weight loss?",
            field: "weightLossExperience",
            options: [
                {
                    id: "lost_want_more",
                    emoji: "📉",
                    text: "I've lost weight before and want to lose more",
                },
                {
                    id: "tried_unsuccessful",
                    emoji: "🔁",
                    text: "I've tried to lose weight before but was unsuccessful",
                },
                {
                    id: "lost_gained_back",
                    emoji: "📈",
                    text: "I've lost weight before but gained it back",
                },
                {
                    id: "never_tried",
                    emoji: "💡",
                    text: "I've never tried to lose weight before",
                },
            ],
        },
        {
            id: "time_since_ideal_weight",
            layout: "single_select",
            title: "How long has it been since you were at your ideal weight?",
            field: "timeSinceIdealWeight",
            options: [
                {
                    id: "0_6_months",
                    title: "0 - 6 months",
                },
                {
                    id: "6_12_months",
                    title: "6 - 12 months",
                },
                {
                    id: "1_3_years",
                    title: "1 - 3 years",
                },
                {
                    id: "more_than_3_years",
                    title: "More than 3 years",
                },
            ],
        },
        {
            id: "weight_gain_causes",
            layout: "multi_select",
            title: "Have you gained weight due to any life events in recent years?",
            subtitle: "Select all that apply",
            field: "weightGainCauses",
            options: [
                {
                    id: "relationship",
                    text: "Marriage or relationship",
                },
                {
                    id: "injury",
                    text: "Injury",
                },
                {
                    id: "social_calendar",
                    text: "Packed social calendar",
                },
                {
                    id: "metabolism",
                    text: "Slower metabolism",
                },
                {
                    id: "mental_health",
                    text: "Added stress or mental health",
                },
                {
                    id: "medication",
                    text: "New medication",
                },
                {
                    id: "pregnancy",
                    text: "Pregnancy",
                },
                {
                    id: "hormonal_changes",
                    text: "Hormonal changes (like menopause)",
                },
                {
                    id: "hormonal_disorder",
                    text: "Hormonal disorder",
                },
                {
                    id: "none",
                    text: "None of the above",
                },
            ],
        },
        {
            id: "fitness_priorities",
            layout: "single_select",
            title: "Which of the below best describes your current priorities?",
            field: "fitnessPriorities",
            options: [
                {
                    id: "weight_loss_only",
                    text: "I am only focused on losing weight",
                },
                {
                    id: "muscle_and_fat_loss",
                    text: "I'd like to gain some muscle while I lose fat",
                },
                {
                    id: "muscle_priority",
                    text: "Gaining muscle is more important to me than losing fat",
                },
            ],
        },
        {
            id: "weight_loss_motivation",
            layout: "single_select",
            title:
                "Is there anything specific motivating you to lose weight right now?",
            field: "weightLossMotivation",
            options: [
                {
                    id: "vacation",
                    emoji: "🏖️",
                    text: "Vacation",
                },
                {
                    id: "wedding",
                    emoji: "💍",
                    text: "Wedding",
                },
                {
                    id: "sports_event",
                    emoji: "🥇",
                    text: "Sports event",
                },
                {
                    id: "summer",
                    emoji: "🌞",
                    text: "Summer",
                },
                {
                    id: "school_reunion",
                    emoji: "🎒",
                    text: "School reunion",
                },
                {
                    id: "family_gathering",
                    emoji: "🏡",
                    text: "Family gathering",
                },
                {
                    id: "birthday",
                    emoji: "🎂",
                    text: "Birthday party",
                },
                {
                    id: "other_occasion",
                    emoji: "👓",
                    text: "Other occasion",
                },
                {
                    id: "general_wellbeing",
                    emoji: "🙅‍♂️",
                    text: "No - just ready to look and feel my best!",
                },
            ],
        },
        {
            id: "weight_prediction",
            layout: "weight_prediction",
            title: "We predict you'll hit",
            buttonText: "Let's make it happen",
        },
        {
            id: "motivation_level",
            layout: "single_select",
            title: "Right now, how motivated are you to reach your happy weight?",
            subtitle: "Understanding your mindset",
            field: "motivationLevel",
            options: [
                {
                    id: "very_motivated",
                    title: "I'm 100% ready",
                },
                {
                    id: "hopeful",
                    title: "I'm pretty hopeful about it",
                },
                {
                    id: "unsure",
                    title: "I'm a bit unsure",
                },
                {
                    id: "relaxed",
                    title: "I'm kinda taking it easy",
                },
            ],
        },
        {
            id: "uncertainty_reason",
            layout: "multi_select",
            title: "What do you think is making you uncertain?",
            subtitle: "Understanding your concerns helps us address them better",
            field: "uncertaintyReason",
            options: [
                {
                    id: "past_failure",
                    text: "I haven't had much luck in the past",
                },
                {
                    id: "restrictive_plans",
                    text: "Previous plans were too restrictive",
                },
                {
                    id: "weight_regain",
                    text: "I worry I'll just gain the weight back",
                },
                {
                    id: "plateau_fear",
                    text: "I think I'll plateau",
                },
                {
                    id: "motivation_concern",
                    text: "Bit worried about my motivation",
                },
                {
                    id: "unrealistic_plan",
                    text: "The plan doesn't seem realistic",
                },
                {
                    id: "not_hardcore",
                    text: "The plan doesn't seem 'hardcore' enough",
                },
                {
                    id: "other",
                    text: "Other",
                },
            ],
        },

        // {
        //   id: "testimonial",
        //   layout: "testimonial",
        //   title: "Staying on track can be hard.",
        //   subtitle: "We'll make it simple.",
        //   userName: "Sheena",
        //   achievement: "28 lbs lost",
        //   testimonial:
        //     "Losing weight was a nightmare for me before I found this app! It's so much easier to track calories and monitor progress with it. I've never seen myself slimmer 💪",
        //   beforeImage: "/w-before.png", // Add these images to your public folder
        //   afterImage: "/w-after.png",
        //   buttonText: "Continue",
        // },
        {
            id: "weight_loss_pace",
            layout: "single_select",
            title: "Your action plan is ready!",
            subtitle:
                "It's designed to work at your pace. So, tell us: How quickly do you want to lose weight?",
            field: "weightLossPace",
            options: [
                {
                    id: "fast",
                    title: "As quickly as possible",
                },
                {
                    id: "slow",
                    title: "Slow and steady does it",
                },
                {
                    id: "moderate",
                    title: "Somewhere between the two",
                },
            ],
            splash: {
                layout: "testimonial",
                title: "Staying on track can be hard.",
                subtitle: "We'll make it simple.",
            },
        },
        {
            id: "email_collection",
            layout: "email_collection",
            title: "Last step - Get your personalized plan",
            subtitle: "Enter your email to receive your custom weight loss plan",
            buttonText: "Get My Plan",
            field: "email",
            showProgress: false,
        },
        {
            id: "paywall",
            layout: "paywall",
            title: "Choose Your Plan",
            subtitle: "Start your journey to a healthier you today",
            showProgress: false,
            hideNavigation: false,
            splash: {
                layout: "final",
                title: "Welcome to Caloria",
                subtitle: "Your personalized weight management companion",
                imageUrl: "/app_icon.png",
                buttonText: "See My Plan Options",
            },
            plans: [
                {
                    id: "monthly",
                    title: "Monthly Plan",
                    price: "$9.99",
                    period: "per month",
                    features: [
                        "Personalized meal plans",
                        "Progress tracking",
                        "Food diary",
                        "Exercise routines",
                        "24/7 support",
                    ],
                    popular: false,
                },
                {
                    id: "yearly",
                    title: "Yearly Plan",
                    price: "$59.99",
                    period: "per year",
                    features: [
                        "Everything in Monthly",
                        "Save 50%",
                        "Premium recipes",
                        "Advanced analytics",
                        "Priority support",
                    ],
                    popular: true,
                },
            ],
        },
    ],
};

export const TOTAL_STEPS = stepsConfig.steps.length;

export const FUNNEL_SECTIONS = [
    {
        id: "basics",
        title: "Basics",
        steps: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
    },
    {
        id: "habits",
        title: "Habits",
        steps: [
            16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33,
            34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47,
        ],
    },
    {
        id: "nutritions",
        title: "Nutritions",
        steps: [48, 49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63],
    },
];

export const getSplashConfig = (stepId) => {
    const step = stepsConfig.steps[stepId];
    return step?.splash || null;
};
